import {
  AnalyticsResalesProps,
  ResalesProductsTotalsSchema,
  DonutChartDataSchema,
  DonutAndTrendSchema,
  TableRowSchema,
  ResalesProductsDimension,
} from "./Resales.type";
import SearchSelect from "@ui-components/SearchSelect";
import { PageSection } from "@ui-components/Container";
import { useState, useEffect } from "react";
import { UserOption } from "@feature/commons";
import {
  fetchAgentResales,
  mapEnterprisesOptions,
} from "@feature/quotations/lib/utilities";
import { useIntl } from "react-intl";
import apiRequest from "@services/apiRequest";
import authenticationService from "@services/AuthenticationService";
import SelectProduct from "@components/SelectProduct";
import { ProductsSchema } from "@feature/types";
import Toggle from "@ui-components/Toggle";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
// Components
import ProgressChart from "@components/Charts/ProgressChart";
import { formatCurrency } from "@feature/quotations/quotation-draft/lib/format";
import TrendChart from "./TrendChart";
import DonutChart from "@components/Charts/DonutChart";
import {
  elaborateProgressChartData,
  elaborateTrendAndDonutChartData,
} from "./Resales.lib";
import { Table } from "./Table";
import Placeholder from "../../../ui-components/Placeholder";

export function AnalyticsResales({
  visibilityLevel,
  visibilityUserId,
  analysisPeriod,
}: AnalyticsResalesProps) {
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance();
  const userData = authenticationService.getUserData();
  const [resalesDefaultOptions, setResalesDefaultOptions] = useState<
    UserOption[]
  >([]);
  const [selectedResales, setSelectedResales] = useState<UserOption[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<{ value: string|number, label:string}[]>(
    []
  );
  const [linesOptions, setLinesOptions] = useState<string[]>([]);
  const [bdgtClsOptions, setBdgtClsOptions] = useState<string[]>([]);
  const [selectedLines, setSelectedLines] = useState<{value:string, label:string}[]>([]);
  const [selectedBdgtCls, setSelectedBdgtCls] = useState<{value:string, label:string}[]>([]);
  const [productsOrResales, setProductsOrResales] = useState<
    ResalesProductsDimension
  >("products");

  const [progressChartData, setProgressChartData] = useState<any>([]);
  const [total, setTotal] = useState(0);
  const [potential, setPotential] = useState(0);

  const [trendChartData, setTrendChartData] = useState([]);
  const [seriesNamesAndColors, setSeriesNamesAndColors] = useState<
    { name: string; color: string }[]
  >([]);
  const [donutChartData, setDonutChartData] = useState<DonutChartDataSchema[]>(
    []
  );
  const [donutTotal, setDonutTotal] = useState(0);
  const [tableData, setTableData] = useState<TableRowSchema[]>([]);
  const [showPotential, setShowPotential] = useState(false);
  const [valueOrQuantity, setValueOrQuantity] = useState<"value" | "quantity">(
    "value"
  );
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [loadingTrendAndDonut, setLoadingTrendAndDonut] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);

  useEffect(() => {
    const fetchLines = async () => {
      const response = await countryApiRequest.get("/products/search", {
        params: {
          column: 'prod_line_cod',
          limit: -1
        }
      });
      setLinesOptions(response.filter((line: string) => ![null, ''].includes(line)).map((line: string) => ({
        value: line, label: line
      })));
    }
    fetchLines();
  }, []);

  useEffect(() => {
    const fetchBdgtCls = async () => {
      const response = await countryApiRequest.post("/products/budget_classes", {
        prod_line_cods: selectedLines.map((line) => line.value)
      });

      setBdgtClsOptions(response.filter((bdgtCls: string) => ![null, ''].includes(bdgtCls)).map((bdgtCls: string) => ({
        value: bdgtCls, label: bdgtCls
      })));
      if (selectedBdgtCls.length > 0) setSelectedBdgtCls([]);
      if (selectedProducts.length > 0) setSelectedProducts([]);
    }
    fetchBdgtCls();
  }, [selectedLines]);

  const fetchResales = async (inputText?: string) => {
    let userId, userRole;
    if (visibilityLevel === 1 && visibilityUserId === 0) {
      // the user is an admin and has not impersonated any user
      userId = undefined;
      userRole = userData.role_type;
    } else {
      // the user is not an admin or has impersonated another user
      userId = visibilityUserId === 0 ? userData.id : visibilityUserId;
      userRole = undefined;
    }
    const result = await fetchAgentResales(
      apiRequest,
      userId,
      inputText,
      10,
      userRole,
      false
    );
    return mapEnterprisesOptions(result.items);
  };

  useEffect(() => {
    const fetchResalesAndSetDefaultOptions = async () => {
      const resales = await fetchResales();
      setResalesDefaultOptions(resales);
    };
    fetchResalesAndSetDefaultOptions();
  }, [visibilityUserId]);

  useEffect(() => {
    async function fetchData() {
      setLoadingProgress(true);
      const payload = {
        resales_ids: selectedResales.map((resale) => resale.value),
        products: selectedProducts.map((product) => `${product.value}_${product.label}`),
        prod_line_cods: selectedLines.map((line) => line.value),
        bdg_cls_cods: selectedBdgtCls.map((bdgtCls) => bdgtCls.value),
        start_date: analysisPeriod.start,
        end_date: analysisPeriod.end,
        visibility_user_id: visibilityUserId,
        internal_users: [],
        filter_ownership: [],
        filter_status: [],
      };
      const res: ResalesProductsTotalsSchema = await countryApiRequest.post(
        "/analytics/resales/totals",
        payload
      );
      const data = elaborateProgressChartData(res);
      setProgressChartData(data);
      setTotal(parseFloat(res?.total?.toFixed(2)));
      setPotential(parseFloat(res?.potential?.toFixed(2)));
      setLoadingProgress(false);
    }
    fetchData();
  }, [
    selectedResales,
    selectedProducts,
    selectedLines,
    selectedBdgtCls,
    analysisPeriod,
    visibilityUserId
  ]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingTrendAndDonut(true);
      const payload = {
        resales_ids: selectedResales.map((resale) => resale.value),
        products: selectedProducts.map((product) => `${product.value}_${product.label}`),
        prod_line_cods: selectedLines.map((line) => line.value),
        bdg_cls_cods: selectedBdgtCls.map((bdgtCls) => bdgtCls.value),
        start_date: analysisPeriod.start,
        end_date: analysisPeriod.end,
        visibility_user_id: visibilityUserId,
        internal_users: [],
        filter_ownership: [],
        filter_status: [],
        dimension: productsOrResales,
      };
      const { trend, donut, table }: DonutAndTrendSchema =
        await countryApiRequest.post(
          "/analytics/resales-products/trends_and_donut",
          payload
        );

      const { trendData, donutData, series } = elaborateTrendAndDonutChartData(
        intl.formatMessage,
        trend,
        donut
      );
      setSeriesNamesAndColors(series);
      setTrendChartData(trendData);
      setDonutChartData(donutData);
      setDonutTotal(donutData.reduce((acc, curr) => acc + curr.value, 0));
      setLoadingTrendAndDonut(false);
    };
    fetchData();
  }, [
    selectedResales,
    selectedProducts,
    selectedLines,
    selectedBdgtCls,
    analysisPeriod,
    visibilityUserId,
    productsOrResales,
  ]);

  return (
    <div className="w-full">
      <div className="flex flex-row gap-12 w-full mb-8">
        <SearchSelect
          label="Rivendite"
          loadOptions={async (
            inputValue: string,
            callback: (options: UserOption[]) => void
          ) => {
            const resales = await fetchResales(inputValue);
            callback(resales);
          }}
          onChange={(e: any) => setSelectedResales(e)}
          value={selectedResales}
          isMulti={true}
          CustomGroup={undefined}
          defaultOptions={resalesDefaultOptions}
          CustomOption={({ data: userOption, innerProps, innerRef }: any) => {
            const { object, label, secondaryLabel } = userOption;
            return (
              <article
                ref={innerRef}
                className="py-2 px-3 cursor-pointer hover:bg-blue-50"
                {...innerProps}
              >
                {!!object && <p className="font-semibold text-sm">{label}</p>}
                <p className={!object ? "text-sm" : "text-xs"}>
                  {secondaryLabel || " "}
                </p>
              </article>
            );
          }}
        />
        <div className={"flex flex-row gap-3 w-full mb-8"}>
          <SearchSelect
            label={intl.formatMessage({id: "lines", defaultMessage: "Linee"})}
            options={linesOptions}
            onChange={setSelectedLines}
            value={selectedLines}
            isMulti={true}
          />
          <SearchSelect
            label={intl.formatMessage({id: "bdgt_cls", defaultMessage: "Classi budget"})}
            options={bdgtClsOptions}
            onChange={setSelectedBdgtCls}
            value={selectedBdgtCls}
            isMulti={true}
          />
          <SelectProduct
            onChange={(
              prodOptions: {
                value: string;
                label: string;
                secondaryLabel: string;
              }[]
            ) => {
              setSelectedProducts(
                prodOptions.map(
                  (prodOption) =>
                    ({
                      value: prodOption.value,
                      label: prodOption.secondaryLabel || prodOption.label,
                    } as ProductsSchema)
                )
              );
            }}
            value={selectedProducts}
            location="ServiceConfiguratorCalculation"
            noZeroPrice={true}
            showArtCod={true}
            isMulti={true}
            businessId={userData.business_id}
            lines={selectedLines.map((line) => line.value)}
            bdgtCls={selectedBdgtCls.map((bdgtCls) => bdgtCls.value)}
            includeKColorProducts={true}
          />
        </div>
      </div>
      <div>
        <p>
          <span className="font-bold">
            {intl.formatMessage({
              id: "total_products",
              defaultMessage: "Totali prodotti kerakoll",
            })}
          </span>
          : {formatCurrency(total)}
        </p>
      </div>
      {!loadingProgress?
        progressChartData.length ? (
        <ProgressChart
          id="kpiResalesProgressChart"
          data={progressChartData}
          intl={intl}
        />
      ) : (
        <p className="my-10 text-xl text-current">
          {intl.formatMessage({
            id: "no_data",
            defaultMessage: "Non ci sono dati da mostrare",
          })}
        </p>
      ):<Placeholder height="h-20"/>}
      <div className="mb-8">
        <p>
          <span className="font-bold">
            {intl.formatMessage({
              id: "potential",
              defaultMessage: "Potenziale",
            })}
          </span>
          : {formatCurrency(potential)}
        </p>
      </div>
      <PageSection
        title={
          <span className="font-bold">
            {intl.formatMessage({
              id: "resales",
              defaultMessage: "Rivendite nel tempo",
            })}
          </span>
        }
        description={undefined}
      >
        {!loadingProgress?<>
          <div className="flex flex-row gap-x-2 font-bolder text-lg">
          <span>
            {intl.formatMessage({id: "resales", defaultMessage: "Rivendite"})}
          </span>
            <div className="-mt-5">
              {/*@ts-ignore*/}
              <Toggle
                checked={productsOrResales === "products"}
                onChange={() => {
                  setProductsOrResales(
                    productsOrResales === "products" ? "resales" : "products"
                  );
                }}
              />
            </div>
            <span>
            {intl.formatMessage({id: "products", defaultMessage: "Prodotti"})}
          </span>
          </div>
          <div className={"flex-row grid grid-cols-5"}>
            <div className={"col-span-3"}>
              {trendChartData.length ? (
                <TrendChart
                  id="kpiResalesTrendChart"
                  data={trendChartData}
                  serieNamesAndColors={seriesNamesAndColors}
                  minHeight={"600px"}
                />
              ) : (
                <p className="my-10 text-xl text-current">
                  {intl.formatMessage({
                    id: "no_data",
                    defaultMessage: "Non ci sono dati da mostrare",
                  })}
                </p>
              )}
            </div>
            <div className={"col-span-2"}>
              {donutChartData.length ? (
                <DonutChart
                  minHeight={"600px"}
                  id={"kpiResalesDonutChart"}
                  data={donutChartData}
                  legendPosition={"bottom"}
                  showPercentages={false}
                  showPercentageInTooltip={true}
                  formatNumber="#.0a"
                  uom={"€"}
                  // otherValueField={'quantityInfo'}
                  otherValueField={valueOrQuantity === 'quantity' ? "quantityInfo" : null}
                />
              ) : (
                <p className="my-10 text-xl text-current">
                  {intl.formatMessage({
                    id: "no_data",
                    defaultMessage: "Non ci sono dati da mostrare",
                  })}
                </p>
              )}
              <div>
                <div className="flex justify-center mt-4 gap-x-2">
                  {donutChartData.length === 0 ? <span className="font-bold">
                    {intl.formatMessage({
                      id: "no_data",
                      defaultMessage: "Non ci sono dati da mostrare",
                    })}
                  </span>
                    :
                    <span className="font-bold">
                      {intl.formatMessage({id: "total", defaultMessage: 'Totale'}) + ': ' + formatCurrency(donutTotal)}
                    </span>
                  }
                </div>
              </div>
            </div>
          </div>
        </>:<Placeholder height="h-96"/>}
      </PageSection>
      {!loadingTable ? <div>
        {productsOrResales == 'products' && <div className={"flex justify-between"}>
          <div className="flex flex-row gap-x-2 font-bolder text-lg">
          <span>
            {intl.formatMessage({id: "dont_show_potential", defaultMessage: "no potenziale"})}
          </span>
            <div className="-mt-5">
              <Toggle
                checked={showPotential}
                onChange={() => {
                  setShowPotential(!showPotential);
                }}
              />
            </div>
            <span>
            {intl.formatMessage({id: "show_potential", defaultMessage: "potenziale"})}
          </span>
          </div>
          <div className="flex flex-row gap-x-2 font-bolder text-lg">
          <span>
            {intl.formatMessage({id: "value", defaultMessage: "€"})}
          </span>
            <div className="-mt-5">
              <Toggle
                checked={valueOrQuantity === "quantity"}
                onChange={() => {
                  setValueOrQuantity(
                    valueOrQuantity === "value" ? "quantity" : "value"
                  );
                }}
              />
            </div>
            <span>
            {intl.formatMessage({id: "quantity", defaultMessage: "Q.tà"})}
          </span>
          </div>
        </div>}
        <Table
          title={intl.formatMessage({
            id: productsOrResales === "products" ? "products" : "resales",
            defaultMessage: productsOrResales === "products" ? "Prodotti" : "Rivendite",
          })}
          showPotential={showPotential}
          productsOrResales={productsOrResales}
          selectedResales={selectedResales}
          selectedProducts={selectedProducts}
          selectedLines={selectedLines}
          selectedBdgtCls={selectedBdgtCls}
          analysisPeriod={analysisPeriod}
          visibilityUserId={visibilityUserId}
          valueOrQuantity={valueOrQuantity}
        />
      </div> : <Placeholder height="h-96"/>}
    </div>
  );
}
